import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import Header from '../../components/Header';
import Appfooter from '../../components/Appfooter';
import Load from '../../components/Load';
import CarrinhoPainel from '../../components/CarrinhoPainel';
import {
  DISPONIBILIDADE_QUADRAS_BATCH,
  TIPOS_PISO,
  TIPO_PISO_LABEL,
  todayISO,
  formatCurrency,
} from '../../services/quadraService';
import { QUADRAS_POR_LOCAL } from '../../services/quadraAdminService';
import { LOCAL_POR_ID } from '../../services/localService';
import { buscarDescontoAlunoParaUsuario } from '../../services/ligaLocalService';

const MEU_SALDO_LOCAL = gql`
  query MeuSaldoNoLocalDetalhe($localId: ID!) {
    meuSaldoNoLocal(localId: $localId)
  }
`;

const addDays = (iso: string, n: number) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

const DIAS_PT = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

// ── Card de quadra com disponibilidade ───────────────────────────────────────

const QuadraCard: React.FC<{ quadra: any; data: string; quadrasIds: string[]; quadrasNomes: string[]; quadrasDisp: React.MutableRefObject<number[]>; idx: number; slots: any[]; loadingSlots: boolean }> = ({ quadra, data, quadrasIds, quadrasNomes, quadrasDisp, idx, slots, loadingSlots }) => {
  const navigate = useNavigate();

  const disponiveis = slots.filter(s => s.disponivel).length;
  const primeiroSlot = slots.find(s => s.disponivel);

  return (
    <div className="col-lg-6 col-xl-4 mb-3">
      <div
        className="card w-100 shadow-xss rounded-xxl border-0 overflow-hidden h-100"
        style={{ cursor: 'pointer' }}
        onClick={() => {
          const ids = quadrasIds.join(',');
          const nomes = quadrasNomes.join(',');
          const disp = quadrasDisp.current.join(',');
          navigate(`/quadras/${quadra.id}?data=${data}&quadras=${ids}&quadrasNomes=${nomes}&quadrasDisp=${disp}&idx=${idx}`);
        }}
      >
        <div style={{
          height: 140,
          background: quadra.imagens?.[0]?.url
            ? `url(${quadra.imagens?.[0]?.url}) center/cover`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          flexShrink: 0,
        }}>
          <span className="badge bg-white text-dark font-xssss fw-600 rounded-xl" style={{ position: 'absolute', top: 8, left: 8 }}>
            {TIPO_PISO_LABEL[quadra.tipoPiso] ?? quadra.tipoPiso}
          </span>
          {!loadingSlots && disponiveis === 0 && (
            <span className="badge bg-danger font-xssss fw-600 rounded-xl" style={{ position: 'absolute', top: 8, right: 8 }}>
              Sem horários
            </span>
          )}
        </div>
        <div className="p-3 d-flex flex-column flex-grow-1">
          <h4 className="fw-700 font-xss mb-1 text-grey-900">{quadra.nome}</h4>
          {quadra.descricao && (
            <p className="font-xssss text-grey-500 mb-2" style={{ lineHeight: 1.4 }}>{quadra.descricao}</p>
          )}
          <div className="d-flex align-items-center justify-content-between mb-3 mt-auto">
            {loadingSlots ? (
              <span className="font-xssss text-grey-400">Verificando...</span>
            ) : (
              <span className={`font-xssss fw-600 ${disponiveis > 0 ? 'text-success' : 'text-danger'}`}>
                <i className="feather-clock me-1"></i>
                {disponiveis > 0
                  ? `${disponiveis} horário${disponiveis !== 1 ? 's' : ''} livre${disponiveis !== 1 ? 's' : ''}`
                  : 'Indisponível'}
              </span>
            )}
            {primeiroSlot && (
              <span className="font-xssss text-grey-500">A partir de {primeiroSlot.horaInicio}</span>
            )}
          </div>
          <button
            className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 w-100"
            disabled={!loadingSlots && disponiveis === 0}
          >
            {disponiveis > 0 ? 'Selecionar horários' : 'Ver quadra'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Tela principal ────────────────────────────────────────────────────────────

const LocalDetalhe: React.FC = () => {
  const { id: localId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [data, setData] = useState(searchParams.get('data') ?? todayISO());
  const [tipoPiso, setTipoPiso] = useState(searchParams.get('tipoPiso') ?? '');
  const [percentualDesconto, setPercentualDesconto] = useState<number | null>(null);

  useEffect(() => {
    if (localId) buscarDescontoAlunoParaUsuario(localId).then(setPercentualDesconto);
  }, [localId]);

  const diaSemana = DIAS_PT[new Date(data + 'T12:00:00').getDay()];

  const setDataNav = (novaData: string) => {
    if (novaData < todayISO()) return;
    setData(novaData);
    const p = new URLSearchParams(searchParams);
    p.set('data', novaData);
    setSearchParams(p, { replace: true });
  };

  const handleTipoPiso = (v: string) => {
    setTipoPiso(v);
    const p = new URLSearchParams(searchParams);
    if (v) p.set('tipoPiso', v); else p.delete('tipoPiso');
    setSearchParams(p, { replace: true });
  };

  const { data: quadrasData, loading } = useQuery(QUADRAS_POR_LOCAL, {
    variables: { localId },
    fetchPolicy: 'cache-and-network',
  });

  // Busca info do local pelo ID (query leve, com cache)
  const { data: localData } = useQuery(LOCAL_POR_ID, {
    variables: { id: localId },
    fetchPolicy: 'cache-first',
  });
  const local = localData?.localPorId;

  const { data: saldoLocalData } = useQuery(MEU_SALDO_LOCAL, {
    variables: { localId },
    skip: !localId,
    fetchPolicy: 'cache-first',
  });
  const saldoCredito: number = saldoLocalData?.meuSaldoNoLocal ?? 0;

  const todasQuadras: any[] = (quadrasData?.quadrasPorLocal ?? []).filter((q: any) => q.ativa);
  const quadras = todasQuadras.filter((q: any) => !tipoPiso || q.tipoPiso === tipoPiso);

  // Batch: busca disponibilidade de todas as quadras em UMA query
  const quadraIds = quadras.map((q: any) => q.id);
  const { data: batchDispData, loading: loadingDisp } = useQuery(DISPONIBILIDADE_QUADRAS_BATCH, {
    variables: { quadraIds, data },
    fetchPolicy: 'network-only',
    skip: quadraIds.length === 0,
  });
  const batchDisp: any[] = batchDispData?.disponibilidadeQuadrasBatch ?? [];

  const dispPorQuadraRef = React.useRef<number[]>([]);
  useEffect(() => {
    if (!loadingDisp && batchDisp.length > 0) {
      dispPorQuadraRef.current = quadras.map((q: any) => {
        const entry = batchDisp.find((d: any) => d.quadraId === q.id);
        return entry ? entry.slots.filter((s: any) => s.disponivel).length : 0;
      });
    }
  }, [loadingDisp, batchDisp, quadras.length]); // eslint-disable-line

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">

            {/* Header */}
            <div className="card shadow-xss w-100 border-0 p-4 mb-3">
              <div className="d-flex align-items-center mb-3 gap-2">
                <button className="btn btn-light btn-sm rounded-xl" onClick={() => navigate(-1)}>
                  <i className="feather-arrow-left me-1"></i> Voltar
                </button>
                <div>
                  <h2 className="fw-700 mb-0 font-md text-grey-900">
                    <i className="bi bi-buildings text-primary me-2"></i>
                    {local?.nome ?? 'Local'}
                  </h2>
                  {local?.endereco && (
                    <p className="font-xssss text-grey-500 mb-0">
                      <i className="feather-map-pin me-1"></i>
                      {local.endereco.bairro}{local.endereco.cidade?.nome ? `, ${local.endereco.cidade.nome}` : ''}
                    </p>
                  )}
                </div>
                {local?.urlLocalizacao && (
                  <a
                    href={local.urlLocalizacao}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-light rounded-xl font-xssss ms-auto px-3"
                  >
                    <i className="feather-map-pin me-1"></i> Ver no mapa
                  </a>
                )}
              </div>

              {percentualDesconto != null && percentualDesconto > 0 && (
                <div className="alert alert-success font-xssss py-2 mb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-percent fw-700"></i>
                  Você tem <strong>{percentualDesconto}% de desconto</strong> como aluno neste local.
                </div>
              )}

              {saldoCredito > 0 && (
                <div className="alert alert-info font-xssss py-2 mb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-wallet2 fw-700"></i>
                  Você tem <strong>{formatCurrency(saldoCredito)}</strong> em crédito neste local. Será aplicado automaticamente no pagamento.
                </div>
              )}

              {/* Filtros */}
              <div className="d-flex flex-wrap gap-2 align-items-end">
                <div>
                  <label className="font-xssss text-grey-500 fw-600 mb-1 d-block">Data</label>
                  <div className="d-flex align-items-center gap-1">
                    <button
                      type="button"
                      className="btn btn-light rounded-xl px-2"
                      style={{ height: 38 }}
                      onClick={() => setDataNav(addDays(data, -1))}
                      disabled={data <= todayISO()}
                    ><i className="feather-chevron-left"></i></button>
                    <input
                      type="date"
                      className="form-control rounded-xl font-xsss"
                      value={data}
                      min={todayISO()}
                      onChange={e => setDataNav(e.target.value || todayISO())}
                      style={{ width: 145, height: 38 }}
                    />
                    <button
                      type="button"
                      className="btn btn-light rounded-xl px-2"
                      style={{ height: 38 }}
                      onClick={() => setDataNav(addDays(data, 1))}
                    ><i className="feather-chevron-right"></i></button>
                    <span className="font-xssss text-grey-500">{diaSemana}</span>
                  </div>
                </div>
                <div>
                  <label className="font-xssss text-grey-500 fw-600 mb-1 d-block">Tipo de Piso</label>
                  <select
                    className="form-select rounded-xl font-xsss"
                    value={tipoPiso}
                    onChange={e => handleTipoPiso(e.target.value)}
                    style={{ width: 160, height: 38 }}
                  >
                    <option value="">Todos</option>
                    {TIPOS_PISO.map(t => (
                      <option key={t} value={t}>{TIPO_PISO_LABEL[t]}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {loading && <Load />}

            {!loading && quadras.length === 0 && (
              <div className="card shadow-xss w-100 p-5 text-center border-0">
                <i className="bi bi-grid-3x3 font-xxl text-grey-300 d-block mb-2"></i>
                <p className="text-grey-500 font-xss mb-0">
                  {tipoPiso ? 'Nenhuma quadra com este tipo de piso.' : 'Nenhuma quadra disponível neste local.'}
                </p>
              </div>
            )}

            <div className="row ps-1 pe-1">
              {quadras.map((q: any, i: number) => {
                const entry = batchDisp.find((d: any) => d.quadraId === q.id);
                const slots = entry?.slots ?? [];
                return (
                  <QuadraCard key={`${q.id}-${data}`} quadra={q} data={data}
                    quadrasIds={quadras.map((x: any) => x.id)}
                    quadrasNomes={quadras.map((x: any) => encodeURIComponent(x.nome))}
                    quadrasDisp={dispPorQuadraRef}
                    idx={i}
                    slots={slots}
                    loadingSlots={loadingDisp}
                  />
                );
              })}
            </div>

          </div>
        </div>
      </div>

      <CarrinhoPainel />
      <Appfooter />
    </Fragment>
  );
};

export default LocalDetalhe;
